import React, { useState, useContext, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Icon } from '@iconify/react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Card,
  CardContent,
  Button,
  Skeleton,
  Grid,
  Tooltip,
  Divider,
} from '@mui/material';
import { GoogleMap, useLoadScript, MarkerF, InfoWindowF } from '@react-google-maps/api';
import { MAP_LIBRARIES } from '../../utils/mapLibraries';
import { AuthContext } from '../../context/AuthContextProvider';
import { useGetFacilities } from '../../api/facilities';
import CentreSelect from '../../components/CentreSelect';
import { BlankPreview } from '../../components/BlankPreview';
import nopreview from '../../assets/images/others/no-preview.png';

const MAPS_API_KEY = 'AIzaSyC2k1lKoyNg_Kj5SoC2z2hCROopjGxB93Y';
const FONT = 'Figtree, sans-serif';
const FACILITY_TYPES =['ambulance'];// ['ambulance', 'icu', 'pharmacy', 'lab', 'other'];

const STATUS_STYLE = {
  available: { bg: '#DAF4E1', color: '#348530', dot: '#348530', label: 'Available' },
  busy: { bg: '#FFF3CD', color: '#E4841A', dot: '#E4841A', label: 'Busy' },
  offline: { bg: '#F0F0F0', color: 'var(--app-text-muted)', dot: '#9e9e9e', label: 'Offline' },
  maintenance: { bg: '#FFEBE8', color: '#D32F2F', dot: '#D32F2F', label: 'Maintenance' },
};

const FACILITY_ICON = {
  ambulance: 'mdi:ambulance',
  icu: 'mdi:hospital-building',
  pharmacy: 'mdi:pill',
  lab: 'mdi:flask',
  other: 'mdi:medical-bag',
};

const FACILITY_COLOR = {
  ambulance: { bg: '#EFF6FF', icon: '#2563EB' },
  icu: { bg: '#FFF0F0', icon: '#D32F2F' },
  pharmacy: { bg: '#F0FDF4', icon: '#348530' },
  lab: { bg: '#FFF8E1', icon: '#E4841A' },
  other: { bg: '#F5F0FF', icon: '#7C3AED' },
};

const DEFAULT_MAP_CENTER = { lat: 20.5937, lng: 78.9629 }; // India center

function StatusDot({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.offline;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: s.dot }} />
      <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: s.color, fontWeight: 600 }}>
        {s.label}
      </Typography>
    </Box>
  );
}

function FacilityCard({ facility, isHighlighted, onHover }) {
  const type = facility.facility_type || 'ambulance';
  const iconKey = FACILITY_ICON[type] || FACILITY_ICON.other;
  const colorKey = FACILITY_COLOR[type] || FACILITY_COLOR.other;

  const hasLocation =
    facility.location?.coordinates?.length === 2 &&
    (facility.location.coordinates[0] !== 0 ||
      facility.location.coordinates[1] !== 0);

  const [lon, lat] = facility.location?.coordinates || [0, 0];

  const mapsUrl = `https://www.google.com/maps?q=${lat},${lon}`;
  const hasTrackingUrl = !!facility.gpsProvider?.trackingUrl;

  return (
    <Card
      elevation={0}
      onMouseEnter={() => onHover(facility._id)}
      onMouseLeave={() => onHover(null)}
      sx={{
        border: isHighlighted
          ? '2px solid #2563EB'
          : '1px solid #EBEBEB',
        borderRadius: '14px',
        transition: 'box-shadow 0.2s, border 0.2s',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        backgroundColor: 'var(--app-surface)',

        '&:hover': {
          boxShadow: 'var(--app-shadow)',
        },
      }}
    >
      <CardContent
        sx={{
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          mb={1.5}
          spacing={1}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '10px',
                backgroundColor: colorKey.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon icon={iconKey} width={24} color={colorKey.icon} />
            </Box>

            <Box>
            <Typography
  sx={{
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: '15px',
    color: 'var(--app-text)',
    lineHeight: 1.3,
    maxWidth: {
      xs: '140px',
      sm: '160px',
      md: '180px',
    },

    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }}
>
  {facility.name || 'Unnamed Facility'}
</Typography>

              <Chip
                label={
                  type.charAt(0).toUpperCase() + type.slice(1)
                }
                size="small"
                sx={{
                  fontFamily: FONT,
                  fontSize: '10px',
                  fontWeight: 600,
                  borderRadius: '5px',
                  backgroundColor: colorKey.bg,
                  color: colorKey.icon,
                  mt: 0.5,
                  height: 18,
                }}
              />
            </Box>
          </Stack>

          <StatusDot status={facility.status} />
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        {/* Content */}
        <Stack spacing={1} mb={2}>
          {(facility.driverName || facility.contactPerson) && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Icon
                icon="mdi:account-outline"
                width={15}
                color="var(--app-text-muted)"
              />

              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: '13px',
                  color: 'var(--app-text)',
                  wordBreak: 'break-word',
                }}
              >
                {facility.driverName ||
                  facility.contactPerson}
              </Typography>
            </Stack>
          )}

          {facility.vehicleNumber && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Icon
                icon="mdi:car-outline"
                width={15}
                color="var(--app-text-muted)"
              />

              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: '13px',
                  color: 'var(--app-text)',
                }}
              >
                {facility.vehicleNumber}
              </Typography>
            </Stack>
          )}

          {facility.notes && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
            >
              <Icon
                icon="mdi:note-text-outline"
                width={15}
                color="var(--app-text-muted)"
                style={{ marginTop: 2 }}
              />

              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: '12px',
                  color: 'var(--app-text-muted)',
                  lineHeight: 1.5,
                  wordBreak: 'break-word',
                }}
              >
                {facility.notes}
              </Typography>
            </Stack>
          )}
        </Stack>

        {/* GPS */}
        {facility.gpsProvider?.name && (
          <Box
            sx={{
              backgroundColor: '#F8FAFC',
              borderRadius: '8px',
              px: 1.5,
              py: 1,
              mb: 2,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              mb={0.3}
            >
              <Icon
                icon="mdi:satellite-uplink"
                width={13}
                color="#348530"
              />

              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#348530',
                }}
              >
                GPS — {facility.gpsProvider.name}
              </Typography>
            </Stack>

            {facility.gpsProvider.deviceId && (
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: '11px',
                  color: 'var(--app-text-muted)',
                  wordBreak: 'break-word',
                }}
              >
                Device: {facility.gpsProvider.deviceId}
              </Typography>
            )}
          </Box>
        )}

        {/* Buttons */}
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          sx={{
            mt: 'auto',
            pt: 1,
            gap: 1,
          }}
        >
          {facility.phone_number && (
            <Button
              variant="contained"
              size="small"
              href={`tel:${facility.phone_number}`}
              startIcon={<Icon icon="mdi:phone" width={14} />}
              sx={{
                fontFamily: FONT,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '12px',
                borderRadius: '8px',
                backgroundColor: '#2563EB',
                color: '#fff',
                flexGrow: 1,
                minWidth: '120px',

                '&:hover': {
                  backgroundColor: '#1d4ed8',
                  color: '#fff',
                },
              }}
            >
              {facility.phone_number}
            </Button>
          )}

          {hasLocation && (
            <Tooltip title="View on Google Maps">
              <Button
                variant="outlined"
                size="small"
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={
                  <Icon
                    icon="mdi:map-marker-outline"
                    width={14}
                  />
                }
                sx={{
                  fontFamily: FONT,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '12px',
                  borderRadius: '8px',
                  borderColor: '#ddd',
                  color: 'var(--app-text)',

                  '&:hover': {
                    borderColor: '#2563EB',
                    color: '#2563EB',
                  },
                }}
              >
                Location
              </Button>
            </Tooltip>
          )}

          {hasTrackingUrl && (
            <Tooltip title="Open live tracking">
              <Button
                variant="outlined"
                size="small"
                href={facility.gpsProvider.trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={
                  <Icon icon="mdi:radar" width={14} />
                }
                sx={{
                  fontFamily: FONT,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '12px',
                  borderRadius: '8px',
                  borderColor: '#A7F3D0',
                  color: '#348530',

                  '&:hover': {
                    borderColor: '#348530',
                    backgroundColor: '#F0FDF4',
                  },
                }}
              >
                Track
              </Button>
            </Tooltip>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function SkeletonCard() {
  return (
    <Card elevation={0} sx={{ border: '1px solid #EBEBEB', borderRadius: '14px' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1.5} mb={2}>
          <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: '10px' }} />
          <Box sx={{ flex: 1 }}><Skeleton variant="text" width="60%" /><Skeleton variant="text" width="30%" /></Box>
        </Stack>
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="50%" />
        <Skeleton variant="rounded" height={34} sx={{ mt: 1.5, borderRadius: '8px' }} />
      </CardContent>
    </Card>
  );
}

function AmbulanceMap({ facilities, hoveredId, onMarkerClick }) {
  const { isLoaded } = useLoadScript({ googleMapsApiKey: MAPS_API_KEY, libraries: MAP_LIBRARIES });
  const [selectedId, setSelectedId] = useState(null);
  const mapRef = useRef(null);

  const onLoad = useCallback((map) => { mapRef.current = map; }, []);

  const withLocation = facilities.filter(
    (f) => f.location?.coordinates?.length === 2 &&
      (f.location.coordinates[0] !== 0 || f.location.coordinates[1] !== 0)
  );

  const mapCenter = withLocation.length > 0
    ? { lat: withLocation[0].location.coordinates[1], lng: withLocation[0].location.coordinates[0] }
    : DEFAULT_MAP_CENTER;

  const statusColor = (status) => ({
    available: '#348530',
    busy: '#E4841A',
    offline: '#9e9e9e',
    maintenance: '#D32F2F',
  }[status] || '#9e9e9e');

  const markerIcon = (facility) => {
    const color = statusColor(facility.status);
    const isActive = hoveredId === facility._id || selectedId === facility._id;
    const size = isActive ? 54 : 44;
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24">
        <circle cx="12" cy="10" r="8" fill="${color}" fill-opacity="0.15" stroke="${color}" stroke-width="1.5"/>
        <text x="12" y="14.5" text-anchor="middle" font-size="10" fill="${color}">🚑</text>
        <circle cx="12" cy="22" r="2" fill="${color}"/>
        <line x1="12" y1="18" x2="12" y2="20" stroke="${color}" stroke-width="1.5"/>
      </svg>`;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: isLoaded ? new window.google.maps.Size(size, size) : undefined,
      anchor: isLoaded ? new window.google.maps.Point(size / 2, size) : undefined,
    };
  };

  if (!isLoaded) {
    return <Skeleton variant="rounded" height={280} sx={{ borderRadius: '12px' }} />;
  }

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '280px', borderRadius: '12px' }}
      center={mapCenter}
      zoom={withLocation.length > 0 ? 13 : 5}
      onLoad={onLoad}
      options={{ disableDefaultUI: false, zoomControl: true, streetViewControl: false, fullscreenControl: true, mapTypeControl: false }}
    >
      {withLocation.map((f) => (
        <MarkerF
          key={f._id}
          position={{ lat: f.location.coordinates[1], lng: f.location.coordinates[0] }}
          icon={markerIcon(f)}
          onClick={() => {
            setSelectedId(f._id);
            onMarkerClick(f._id);
          }}
        />
      ))}

      {selectedId && (() => {
        const f = withLocation.find((x) => x._id === selectedId);
        if (!f) return null;
        const s = STATUS_STYLE[f.status] || STATUS_STYLE.offline;
        return (
          <InfoWindowF
            position={{ lat: f.location.coordinates[1], lng: f.location.coordinates[0] }}
            onCloseClick={() => setSelectedId(null)}
          >
            <Box sx={{ fontFamily: FONT, minWidth: 160, p: 0.5 }}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '14px', mb: 0.5 }}>
                {f.name}
              </Typography>
              <Box sx={{ display: 'inline-block', px: 1, py: 0.2, borderRadius: '5px', backgroundColor: s.bg, mb: 0.5 }}>
                <Typography sx={{ fontFamily: FONT, fontSize: '11px', color: s.color, fontWeight: 600 }}>
                  {s.label}
                </Typography>
              </Box>
              {(f.driverName || f.contactPerson) && (
                <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: 'var(--app-text)', mt: 0.3 }}>
                  {f.driverName || f.contactPerson}
                </Typography>
              )}
              {f.phone_number && (
                <a href={`tel:${f.phone_number}`} style={{ fontFamily: FONT, fontSize: '13px', color: '#2563EB', fontWeight: 700, display: 'block', marginTop: 4 }}>
                  📞 {f.phone_number}
                </a>
              )}
            </Box>
          </InfoWindowF>
        );
      })()}
    </GoogleMap>
  );
}

export default function ViewFacilities() {
  const { user } = useContext(AuthContext);

  const [selectedCentreId, setSelectedCentreId] = useState(user?.selectedCenter || '');
  const [filterType, setFilterType] = useState('ambulance');
  const [hoveredId, setHoveredId] = useState(null);

  const { facilities, isLoading } = useGetFacilities({
    facility_type: filterType,
    centerId: selectedCentreId,
  });

  const availableCount = facilities.filter((f) => f.status === 'available').length;
  const withLocationCount = facilities.filter(
    (f) => f.location?.coordinates?.[0] !== 0 || f.location?.coordinates?.[1] !== 0
  ).length;

  const handleCentreChange = (opt) => setSelectedCentreId(opt?.value || '');

  return (
    <Box className="main-wrappers">
      <Box className="page-wrapper" sx={{ pb: '24px', fontFamily: FONT }}>
        <Box className="content">
          <Helmet><title>Facilities | Spandan One</title></Helmet>

          {/* Header */}
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'flex-start' }} justifyContent="space-between" mb={3} gap={2}>
            <Box>
              <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '22px', color: 'var(--app-text)' }}>
                Facilities
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: 'var(--app-text-muted)', mt: 0.3 }}>
                {!isLoading && facilities.length > 0
                  ? `${facilities.length} total · ${availableCount} available · ${withLocationCount} on map`
                  : 'Ambulances and facility details for your centre'}
              </Typography>
            </Box>

            {/* Filters row */}
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              {/* Type filter chips */}
              <Stack direction="row" spacing={0.5}>
                {FACILITY_TYPES.map((t) => (
                  <Chip
                    key={t}
                    label={t.charAt(0).toUpperCase() + t.slice(1)}
                    onClick={() => setFilterType(t)}
                    variant={filterType === t ? 'filled' : 'outlined'}
                    size="small"
                    sx={{
                      fontFamily: FONT, fontWeight: 600, fontSize: '12px', cursor: 'pointer',
                      borderRadius: '6px',
                      backgroundColor: filterType === t ? '#2563EB' : 'transparent',
                      color: filterType === t ? '#fff' : 'var(--app-text)',
                      borderColor: filterType === t ? '#2563EB' : '#ddd',
                      '&:hover': { backgroundColor: filterType === t ? '#1d4ed8' : 'var(--app-surface-2)' },
                    }}
                  />
                ))}
              </Stack>
            </Stack>
          </Stack>

          {/* Centre filter */}
          <Box sx={{ mb: 2.5, maxWidth: 320 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: 'var(--app-text)', mb: 0.5 }}>
              Select Centre
            </Typography>
            <CentreSelect
              valueId={selectedCentreId}
              onChange={handleCentreChange}
              placeholder="Select Centre"
            />
          </Box>

          {/* Map */}
          {!isLoading && facilities.length > 0 && (
            <Box mb={3}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Icon icon="mdi:map-outline" width={18} color="#2563EB" />
                <Typography sx={{ fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: 'var(--app-text)' }}>
                  Live Map
                </Typography>
                {withLocationCount === 0 && (
                  <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: '#aaa' }}>
                    (no GPS coordinates set)
                  </Typography>
                )}
              </Stack>
              <AmbulanceMap
                facilities={facilities}
                hoveredId={hoveredId}
                onMarkerClick={(id) => setHoveredId(id)}
              />
            </Box>
          )}

          {/* Cards */}
          {isLoading ? (
            <Grid container spacing={2}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <SkeletonCard />
                </Grid>
              ))}
            </Grid>
          ) : facilities.length === 0 ? (
            <Box sx={{ mt: 4 }}>
              <BlankPreview icon={nopreview} title="No facilities found" subtitle="No facilities are registered for this centre yet" />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {facilities.map((f) => (
                <Grid item xs={12} sm={6} md={4} key={f._id}>
                  <FacilityCard
                    facility={f}
                    isHighlighted={hoveredId === f._id}
                    onHover={setHoveredId}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>
    </Box>
  );
}
